import { findKey } from 'lodash';
import pigretto, { apply, call } from 'pigretto';
import {
  ClearWaitingQueueParams,
  toast,
  ToastContent,
  ToastOptions,
} from 'react-toastify';

interface PigretToast {
  dismissAll: (containerId: string | number) => void;
  getAllToastIds: (containerId: string) => string[];
}

type BaseToast = typeof toast & PigretToast;

const toastContainerIdToastIdsMap: Record<
  string | number,
  Array<string | number>
> = {};

const onToastInvoke =
  (proceed: any) =>
  (...params: [ToastContent, ToastOptions | undefined]) => {
    // `params` contains the parameters passed to the called method.
    const options = params[1] || {};
    const { containerId = '' } = options;
    toastContainerIdToastIdsMap[containerId] =
      toastContainerIdToastIdsMap[containerId] || [];

    // proceed with the call of the original method
    proceed(params, (toastId: string | number) => {
      toastContainerIdToastIdsMap[containerId].push(toastId);

      return toastId;
    });
  };

// Extend the basic functionality of toast by intercepting the method calls
const pigrettoast = pigretto(toast, [
  [
    // Intercept all the calls to a method called "success", "error", "info"
    // matching the `/^warn(ing)?$/` regular expression
    ['success', 'error', 'info', /^warn(ing)?$/],
    call().around(onToastInvoke),
  ],
  [
    // Intercept plain toast execution
    [],
    apply().around(onToastInvoke),
  ],
  [
    // Intercept dismiss in order to remove toastId from container
    ['dismiss'],
    call().around((proceed: any) => (id: string | number) => {
      // find containerId of dismissed toast
      const containerId = findKey(toastContainerIdToastIdsMap, (container) =>
        container.includes(id),
      );

      if (containerId) {
        toastContainerIdToastIdsMap[containerId] = toastContainerIdToastIdsMap[
          containerId
        ].filter((toastId) => toastId !== id);
      }

      proceed(id);
    }),
  ],
  [
    // Intercept clearWaitingQueue in order to remove queue from container
    ['clearWaitingQueue'],
    call().around((proceed: any) => (params: ClearWaitingQueueParams = {}) => {
      const { containerId } = params;

      if (containerId) {
        const container = toastContainerIdToastIdsMap[containerId];

        const waitingQueue = container.filter(
          (toastId) => !toast.isActive(toastId),
        );

        toastContainerIdToastIdsMap[containerId] = toastContainerIdToastIdsMap[
          containerId
        ].filter((toastId) => !waitingQueue.includes(toastId));
      }

      proceed(params);
    }),
  ],
]);

// Close all the toasts of the specific container
pigrettoast.dismissAll = (containerId: string | number) => {
  const toastIds = Object.values(
    toastContainerIdToastIdsMap[containerId] || [],
  );

  toast.clearWaitingQueue({ containerId });

  for (const toastId of toastIds) {
    toast.dismiss(toastId);
  }

  delete toastContainerIdToastIdsMap[containerId];
};

// Get all the toast ids of the specific container
pigrettoast.getAllToastIds = (containerId: string): Array<string | number> => {
  const toastIds =
    Object.values(toastContainerIdToastIdsMap[containerId] || {}) || [];

  return toastIds;
};

export default pigrettoast as BaseToast;
